"use client";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { usePathname } from "next/navigation";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useRouter } from "@bprogress/next";
import Button from "@/components/library/Button";
import Icon, { ICON_SIZES, ICON_TYPE } from "@/components/library/Icon";
import { Progress } from "@/components/ui/progress";
import { useTrialUsage } from "@/hooks/useTrialUsage";
import {
  type TrialUsageProgressColor,
  formatTrialDurationShort,
  pickTrialProgressColor,
} from "@/utils/trialUsage";

type Variant = "sidebar" | "full";

type Props = {
  variant?: Variant;
  className?: string;
};

/**
 * A single `label — n/max` row with its own mini progress bar. Used for both
 * axes (call minutes, conversations) in the expanded sidebar view and in the
 * full Billing page variant.
 */
const UsageRow: React.FC<{
  label: string;
  value: string;
  percent: number;
  compact?: boolean;
}> = ({ label, value, percent, compact }) => {
  const color: TrialUsageProgressColor = pickTrialProgressColor(percent);
  return (
    <div className={clsx("flex", "flex-col", compact ? "gap-1" : "gap-2")}>
      <div
        className={clsx(
          "flex",
          "items-baseline",
          "justify-between",
          "gap-2",
          compact ? "text-[11px]" : "text-sm"
        )}
      >
        <span className={clsx(compact && "text-600")}>{label}</span>
        <span
          className={clsx(compact ? "font-medium" : "text-600", {
            "text-red-500": color === "red",
            "text-yellow-600": color === "yellow",
          })}
        >
          {value}
        </span>
      </div>
      <Progress value={percent} size={compact ? "sm" : "md"} color={color} />
    </div>
  );
};

const TrialUsageWidget: React.FC<Props> = ({ variant = "sidebar", className }) => {
  const { t } = useTranslation("trialUsage");
  const router = useRouter();
  const pathname = usePathname();
  const {
    usage,
    shouldShow,
    callsPercent,
    conversationsPercent,
    usagePercent,
  } = useTrialUsage();
  const [expanded, setExpanded] = React.useState(false);

  if (!shouldShow || !usage) return null;

  // Route users to the plan picker rather than opening the Stripe billing
  // portal — on /get-gail they can choose monthly vs yearly and pick which
  // products (GailGPT, Gail Agent, or both) they want to subscribe to.
  // Pass the current path as `returnUrl` so /get-gail can render a "Back to
  // portal" button that sends the user back to where they came from.
  const onUpgrade = () => {
    const target = pathname
      ? `/get-gail?returnUrl=${encodeURIComponent(pathname)}`
      : "/get-gail";
    router.push(target);
  };
  const summaryColor = pickTrialProgressColor(usagePercent);
  const summaryPercent = Math.round(usagePercent);
  const callsValue = `${formatTrialDurationShort(usage.totalCallSeconds)} / ${formatTrialDurationShort(usage.maxTotalCallSeconds)}`;
  const conversationsValue = `${usage.totalConversations} / ${usage.maxTotalConversations}`;

  if (variant === "sidebar") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, ease: "easeOut" }}
        layout
        className={clsx(
          "rounded-xl",
          "border",
          "border-neutral",
          "bg-panel",
          "px-3",
          "py-3",
          "flex",
          "flex-col",
          "gap-2",
          "overflow-hidden",
          className
        )}
      >
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className={clsx(
            "flex",
            "items-center",
            "justify-between",
            "gap-2",
            "w-full",
            "text-left",
            "bg-transparent",
            "border-0",
            "p-0",
            "cursor-pointer",
            "hover:text-primary",
            "transition-colors"
          )}
        >
          <span className={clsx("text-xs", "text-600")}>{t("trialUsage")}</span>
          <span className={clsx("flex", "items-center", "gap-1")}>
            <motion.span
              key={summaryPercent}
              initial={{ opacity: 0, y: -2 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className={clsx("text-sm", "font-semibold", {
                "text-red-500": summaryColor === "red",
                "text-yellow-600": summaryColor === "yellow",
              })}
            >
              {summaryPercent}%
            </motion.span>
            <motion.span
              animate={{ rotate: expanded ? 180 : 0 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className={clsx("inline-flex", "items-center", "justify-center")}
            >
              <Icon
                icon="chevron-down"
                size={ICON_SIZES.XXS}
                type={ICON_TYPE.SOLID}
                className="text-600"
              />
            </motion.span>
          </span>
        </button>

        <AnimatePresence mode="wait" initial={false}>
          {expanded ? (
            <motion.div
              key="detail"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              className="overflow-hidden"
            >
              <div className={clsx("flex", "flex-col", "gap-3", "pt-1", "pb-1")}>
                <UsageRow
                  label={t("callsLegend")}
                  value={callsValue}
                  percent={callsPercent}
                  compact
                />
                <UsageRow
                  label={t("conversationsLegend")}
                  value={conversationsValue}
                  percent={conversationsPercent}
                  compact
                />
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="summary"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              className="overflow-hidden"
            >
              <Progress value={usagePercent} size="md" color={summaryColor} />
            </motion.div>
          )}
        </AnimatePresence>

        <Button type="secondary" compact wide onClick={onUpgrade}>
          {t("upgrade")}
        </Button>
      </motion.div>
    );
  }

  // Full variant — shown on the Billing page.
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className={clsx(
        "rounded-xl",
        "border",
        "border-neutral",
        "bg-panel",
        "p-5",
        "flex",
        "flex-col",
        "gap-5",
        className
      )}
    >
      <div className={clsx("flex", "items-start", "justify-between", "gap-4")}>
        <div>
          <h3 className={clsx("text-lg", "font-semibold")}>{t("fullTitle")}</h3>
          <p className={clsx("text-sm", "text-600", "mt-1")}>{t("fullSubtitle")}</p>
        </div>
        <Button type="primary" onClick={onUpgrade} iconBefore="arrow-up-right">
          {t("upgrade")}
        </Button>
      </div>

      <UsageRow label={t("callsLegend")} value={callsValue} percent={callsPercent} />
      <UsageRow
        label={t("conversationsLegend")}
        value={conversationsValue}
        percent={conversationsPercent}
      />

      <AnimatePresence>
        {usage.isLimitReached && (
          <motion.p
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2 }}
            className={clsx("text-sm", "text-red-500", "font-medium")}
          >
            {t("limitReachedDetail")}
          </motion.p>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default TrialUsageWidget;
